// Config de la marca de agua por marca (brand_profiles.watermark_config, jsonb).
//
// Todo en PORCENTAJES de la imagen destino (centro-based, como el scene model de
// edicionGrafica): la misma config escala a cualquier resolución y formato. La
// lectura es DEFENSIVA (normalize + clamps + defaults): una config vieja o
// corrupta nunca rompe el flujo de descarga.

import { supabase } from '../lib/supabase';
import { DEFAULT_FONT_FAMILY } from '../pages/edicionGrafica/lib/fonts';

export type WatermarkLogoMode = 'original' | 'no-bg' | 'white';

export interface WatermarkLogoConfig {
  visible: boolean;
  xPct: number;
  yPct: number;
  wPct: number;
  rotation: number;
  opacity: number; // 0..1
  mode: WatermarkLogoMode;
  /** PNG transparente ya procesado por el backend (null hasta tocar "Quitar fondo"). */
  noBgUrl: string | null;
  /** logo_url del que se derivó noBgUrl: si la marca cambió el logo, se invalida. */
  noBgSourceUrl: string | null;
}

export interface WatermarkTextConfig {
  visible: boolean;
  text: string;
  xPct: number;
  yPct: number;
  wPct: number;
  rotation: number;
  fontFamily: string;
  fontSizePct: number; // % del ALTO destino (consistente con el scene model)
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  color: string;
  opacity: number; // 0..1
}

export interface WatermarkConfig {
  version: 1;
  enabled: boolean;
  logo: WatermarkLogoConfig;
  text: WatermarkTextConfig;
}

export function defaultWatermarkConfig(brandName: string): WatermarkConfig {
  return {
    version: 1,
    enabled: true,
    logo: {
      visible: true,
      xPct: 86,
      yPct: 90,
      wPct: 20,
      rotation: 0,
      opacity: 0.6,
      mode: 'original',
      noBgUrl: null,
      noBgSourceUrl: null,
    },
    text: {
      visible: true,
      text: brandName.trim() || 'Mi marca',
      xPct: 50,
      yPct: 50,
      wPct: 60,
      rotation: 0,
      fontFamily: DEFAULT_FONT_FAMILY,
      fontSizePct: 6,
      fontWeight: 'normal',
      fontStyle: 'normal',
      color: '#FFFFFF',
      opacity: 0.18,
    },
  };
}

const clamp = (v: unknown, lo: number, hi: number, dflt: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : dflt;

const pick = <T extends string>(v: unknown, allowed: readonly T[], dflt: T): T =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : dflt;

const bool = (v: unknown, dflt: boolean): boolean => (typeof v === 'boolean' ? v : dflt);

const str = (v: unknown, dflt: string | null, max = 3000): string | null =>
  typeof v === 'string' && v.trim() ? v.slice(0, max) : dflt;

/** Merge defensivo de un jsonb crudo sobre los defaults. null = no hay nada usable. */
export function normalizeWatermarkConfig(raw: unknown): WatermarkConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.logo !== 'object' && typeof r.text !== 'object') return null;
  const d = defaultWatermarkConfig('');
  const logo = (r.logo ?? {}) as Record<string, unknown>;
  const text = (r.text ?? {}) as Record<string, unknown>;
  return {
    version: 1,
    enabled: bool(r.enabled, d.enabled),
    logo: {
      visible: bool(logo.visible, d.logo.visible),
      xPct: clamp(logo.xPct, 0, 100, d.logo.xPct),
      yPct: clamp(logo.yPct, 0, 100, d.logo.yPct),
      wPct: clamp(logo.wPct, 1, 100, d.logo.wPct),
      rotation: clamp(logo.rotation, 0, 360, d.logo.rotation),
      opacity: clamp(logo.opacity, 0, 1, d.logo.opacity),
      mode: pick(logo.mode, ['original', 'no-bg', 'white'] as const, d.logo.mode),
      noBgUrl: str(logo.noBgUrl, null),
      noBgSourceUrl: str(logo.noBgSourceUrl, null),
    },
    text: {
      visible: bool(text.visible, d.text.visible),
      text: (str(text.text, null, 120) ?? d.text.text).trim() || d.text.text,
      xPct: clamp(text.xPct, 0, 100, d.text.xPct),
      yPct: clamp(text.yPct, 0, 100, d.text.yPct),
      wPct: clamp(text.wPct, 1, 100, d.text.wPct),
      rotation: clamp(text.rotation, 0, 360, d.text.rotation),
      fontFamily: str(text.fontFamily, d.text.fontFamily, 120) ?? d.text.fontFamily,
      fontSizePct: clamp(text.fontSizePct, 2, 40, d.text.fontSizePct),
      fontWeight: pick(text.fontWeight, ['normal', 'bold'] as const, d.text.fontWeight),
      fontStyle: pick(text.fontStyle, ['normal', 'italic'] as const, d.text.fontStyle),
      color: str(text.color, d.text.color, 40) ?? d.text.color,
      opacity: clamp(text.opacity, 0, 1, d.text.opacity),
    },
  };
}

// ── Persistencia (brand_profiles.watermark_config) ──────────────────────────
// Cache en memoria por brandId (patrón onboardingSeen): la usan el editor y CADA
// descarga — sin cache, una descarga masiva pegaría N veces a la DB.

const cache = new Map<string, WatermarkConfig | null>();

export function invalidateWatermarkConfigCache(): void {
  cache.clear();
}

// Cambio de marca activa o edición de la marca (nombre/logo) → la config cacheada
// puede ser de otra marca o estar derivada de un logo viejo.
if (typeof window !== 'undefined') {
  window.addEventListener('atlas:brand-changed', invalidateWatermarkConfigCache);
}

export async function fetchWatermarkConfig(brandId: string): Promise<WatermarkConfig | null> {
  if (cache.has(brandId)) return cache.get(brandId) ?? null;
  try {
    const { data, error } = await supabase
      .from('brand_profiles')
      .select('watermark_config')
      .eq('id', brandId)
      .maybeSingle();
    const config = error ? null : normalizeWatermarkConfig(data?.watermark_config);
    cache.set(brandId, config);
    return config;
  } catch {
    return null; // sin config ⇒ la descarga sigue como siempre
  }
}

export async function saveWatermarkConfig(
  brandId: string,
  config: WatermarkConfig,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('brand_profiles')
    .update({ watermark_config: config })
    .eq('id', brandId);
  if (error) return { ok: false, error: error.message };
  cache.set(brandId, config);
  return { ok: true };
}

// ── Última elección del chooser de descarga (por dispositivo) ────────────────

export type WatermarkChoice = 'con' | 'sin';

const CHOICE_KEY = 'atlas:watermark-download-choice';

export function getLastWatermarkChoice(): WatermarkChoice {
  try {
    return localStorage.getItem(CHOICE_KEY) === 'sin' ? 'sin' : 'con';
  } catch {
    return 'con';
  }
}

export function setLastWatermarkChoice(c: WatermarkChoice): void {
  try {
    localStorage.setItem(CHOICE_KEY, c);
  } catch {
    /* modo privado / storage lleno: la preferencia no se recuerda, nada más */
  }
}
